import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, 
  Shield, 
  Users, 
  Target,
  Zap,
  Globe,
  Lock,
  Settings,
  FileText,
  BarChart,
  MessageSquare,
  CheckCircle,
  Server,
  Database,
  Code,
  Key
} from 'lucide-react';

export default function CompanyAboutPage() {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background border-b">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25 dark:[mask-image:linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0))]" style={{ backgroundSize: '30px 30px' }} />
        <div className="relative container px-4 md:px-6 flex flex-col md:flex-row items-center justify-between min-h-[calc(100vh-4rem)] gap-8 py-12">
          <div className="flex-1 text-center md:text-left max-w-2xl">
            <Badge variant="outline" className="mb-4">Our Mission</Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-4">
              Stop Wasting Time on{' '}
              <span className="text-primary">Hiring</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              NewLoop uses AI to find your perfect hires before you post a job. Cut time-to-hire in half, eliminate bias, and build a better team. We're reinventing recruiting for startups.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Button asChild size="lg" className="text-base">
                <Link to="/register">
                  Get Early Access
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base">
                <Link to="/pricing">
                  View Pricing
                </Link>
              </Button>
            </div>
          </div>
          <div className="flex-1 relative max-w-xl">
            <img 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80" 
              alt="Team collaboration"
              className="rounded-lg shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 md:px-6">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">Our Values</h2>
            <p className="mt-4 text-muted-foreground md:text-lg">
              Built on a foundation of transparency, security, and fairness
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Lock className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Data Privacy & Security</h3>
                <p className="text-muted-foreground mb-4">
                  Your data is your data. We exceed GDPR and CCPA requirements with enterprise-grade security.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>End-to-end encryption</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>SOC 2 compliance</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>No hidden data selling</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Code className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Transparent AI</h3>
                <p className="text-muted-foreground mb-4">
                  Our algorithms are designed to be fair, explainable, and actively mitigate bias.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Bias detection & mitigation</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Explainable decisions</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Regular fairness audits</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Results-Driven</h3>
                <p className="text-muted-foreground mb-4">
                  Quantifiable improvements in your hiring process and outcomes.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>50% faster time-to-hire</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>30% lower hiring costs</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>90% candidate satisfaction</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 px-4 md:px-6 bg-muted/50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <img
                    src="https://gexuxqaunwsfohpxacrp.supabase.co/storage/v1/object/public/marketing/Headshot.jpeg"
                    alt="Jonathan Daniels"
                    className="w-32 h-32 rounded-full object-cover border-2 border-border"
                  />
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-2xl font-bold">Jonathan Daniels</h3>
                        <p className="text-primary font-medium">Founder & CEO</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <a href="https://linkedin.com/in/jonathanrdaniels" target="_blank" rel="noopener noreferrer">
                            LinkedIn
                          </a>
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <a href="https://github.com/J0nrages" target="_blank" rel="noopener noreferrer">
                            GitHub
                          </a>
                        </Button>
                      </div>
                    </div>
                    <p className="text-muted-foreground mb-4">
                      Having worked with high-growth startups and scaling companies, Jonathan saw firsthand how broken the hiring process is—relying on biased filters, outdated resumes, and slow, inefficient systems.
                    </p>
                    <p className="text-muted-foreground mb-4">
                      That's why he built NewLoop—to fix hiring at its core. Jonathan believes hiring should be transparent, ethical, and empowering—for both candidates and companies. His expertise combines data-driven decision-making, deep recruiting knowledge, and cutting-edge AI, making him uniquely positioned to lead this transformation.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-6 bg-primary text-primary-foreground">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
              Ready to Transform Your Hiring?
            </h2>
            <p className="mt-4 text-xl text-primary-foreground/90">
              Join leading startups using NewLoop to build amazing teams
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-base"
                asChild
              >
                <Link to="/register">
                  Get Early Access
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-transparent border-white hover:bg-white/10 text-base"
                asChild
              >
                <Link to="/contact">
                  Book a Demo
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}